import { useState, useEffect, useRef, useCallback } from "react";
import { ArticleExtraData } from "../../../../../../../articlePage/stateInterfaces";
import { NewsFlashSpecialDesktopPopupItem } from "./newsFlashSpecialDesktopPopupItem";
import { getMonthTranslation, getTranslation } from "../../../../autoTickerRowWidget/autoTickerRowTranslations";
import { LinkTargetProperties } from "../../../../../../../global/components/linkSettings/interfaces";
import { HyperLinkBuilder } from "../../../../../../commonComponents/inline/hyperLink";

require("./ynet_site_newsFlashSpecialDesktopPopup.less");

interface NewsFlashSpecialDesktopPopupProps {
    isOpen: boolean;
    itemList: ArticleExtraData[];
    hasNewFlash: boolean;
    languageCode: string;
    toAllFlashesLink: string | undefined;
    highlightingDuration: number;
    toAllFlashesLinkTarget: LinkTargetProperties | undefined;
    onClose: () => void;
    highlightedArticleId?: string | null;
}

export const NewsFlashSpecialDesktopPopup = (props: NewsFlashSpecialDesktopPopupProps) => {

    const { isOpen, itemList, languageCode, highlightingDuration, highlightedArticleId, hasNewFlash, onClose, toAllFlashesLink, toAllFlashesLinkTarget } = props;
    const popupRef = useRef<HTMLDivElement>(null) as any;
    const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
    const [newFlashNotification, setNewFlashNotification] = useState<{ phase: "dots" | "title" | "none"; title: string; articleId: string }>({ phase: "none", title: "", articleId: "" });
    const itemRefs: any = useRef<Record<string, HTMLDivElement | null>>({});
    const listContainerRef: any = useRef<HTMLDivElement>(null) as any;
    const notificationTimersRef: any = useRef<{ dots?: ReturnType<typeof setTimeout>; title?: ReturnType<typeof setTimeout> }>({});
    const prevFirstItemKeyRef: any = useRef<string>("");
    const lang = languageCode ? languageCode.slice(0, 2) : "he";
    const toAllFlashesTitle = getTranslation(lang, "ToFullFlashesPage");

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && highlightedArticleId) {
            setExpandedItemId(highlightedArticleId);

            setTimeout(() => {
                const el = itemRefs.current[highlightedArticleId];
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 100);
        }
    }, [isOpen, highlightedArticleId]);

    useEffect(() => {
        if (!isOpen || !hasNewFlash || itemList.length === 0) return;

        const newItem = itemList[0];
        const currentKey = newItem.articleId + "_" + new Date(newItem.dateUpdatedOnSite).getTime();

        if (currentKey === prevFirstItemKeyRef.current) return;

        prevFirstItemKeyRef.current = currentKey;

        clearTimeout(notificationTimersRef.current.dots);
        clearTimeout(notificationTimersRef.current.title);

        setNewFlashNotification({ phase: "dots", title: newItem.title, articleId: newItem.articleId });

        notificationTimersRef.current.dots = setTimeout(() => {
            setNewFlashNotification({ phase: "title", title: newItem.title, articleId: newItem.articleId });
        }, 3000);

        notificationTimersRef.current.title = setTimeout(() => {
            setNewFlashNotification({ phase: "none", title: "", articleId: "" });
        }, 8000);
    }, [hasNewFlash, itemList]);

    const setItemRef = useCallback((articleId: string) => (el: HTMLDivElement | null) => {
        itemRefs.current[articleId] = el;
    }, []);

    const handleItemClick = (articleId: string) => {
        setExpandedItemId(expandedItemId === articleId ? null : articleId);
    };

    const handleNotificationClick = () => {
        const { articleId } = newFlashNotification;
        if (!articleId) return;

        setExpandedItemId(articleId);
        setNewFlashNotification({ phase: "none", title: "", articleId: "" });

        if (listContainerRef.current) {
            listContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }

        setTimeout(() => {
            const el = itemRefs.current[articleId];
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 100);
    };

    const getFormattedDate = () => {
        const now = new Date();
        const day = now.getDate();
        const month = getMonthTranslation(lang, (now.getMonth()));
        const year = now.getFullYear();

        return `${day} ${month} ${year}`;
    };

    return (
        <>
            <div className={`newsflash-popup-overlay ${isOpen ? "open" : ""}`} />

            <div className={`newsflash-popup-container ${isOpen ? "open" : ""}`} ref={popupRef}>
                <div className="popup-header">
                    <button className="popup-close-btn" onClick={onClose}></button>

                    {newFlashNotification.phase !== "none" &&
                        <div className="new-flash-notification" onClick={handleNotificationClick}>
                            {newFlashNotification.phase === "dots" &&
                                <video className="notification-dots" src={"https://www.ynet.co.il/images/picsrv/3-dots-30x30.webm"} autoPlay loop muted playsInline />
                            }

                            {newFlashNotification.phase === "title" &&
                                <div className="notification-title">
                                    <video className="notification-bell" src={"https://www.ynet.co.il/images/picsrv/Bell-Notification-30x30.webm"} autoPlay loop muted playsInline />
                                    <span className="notification-text">{newFlashNotification.title}</span>
                                </div>
                            }
                        </div>
                    }

                    <div className="popup-title">מבזקים</div>

                    <div className="popup-date">{getFormattedDate()}</div>
                </div>

                <div className="popup-items-list" ref={listContainerRef}>
                    {itemList.map((article) => (
                        <NewsFlashSpecialDesktopPopupItem
                            article={article}
                            key={article.articleId}
                            languageCode={languageCode}
                            onItemClick={handleItemClick}
                            toAllFlashesLink={toAllFlashesLink}
                            itemRef={setItemRef(article.articleId)}
                            highlightingDuration={highlightingDuration}
                            toAllFlashesLinkTarget={toAllFlashesLinkTarget}
                            isExpanded={expandedItemId === article.articleId}
                        />
                    ))}

                    <HyperLinkBuilder className="to-all-flashes-popup-btn" href={toAllFlashesLink ? toAllFlashesLink : "#"} linkTarget={toAllFlashesLinkTarget? toAllFlashesLinkTarget : {} as LinkTargetProperties}>
                        <div className="btn-title">{toAllFlashesTitle}</div>
                        <div className="btn-arrow"></div>
                    </HyperLinkBuilder>
                </div>
            </div>
        </>
    );
};
